import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReportTable from "../../../components/report/report-table";
import { OverallStudentReportService } from "../../../services/reports";
import ScoreView from "../../../components/UI/ScoreView";
import DoubleUpArrow from "../../../components/UI/svg/double-up-arrows";
import SingleUpArrow from "../../../components/UI/svg/single-up-arrow";
import * as configActions from "../../../store/config/actions";
import { useDispatch } from "react-redux";
import Notation from "../../../components/report/notation";
import { handleCompPerformance } from "../../../helpers";
import { textIdentifier } from "../../../textIdentifier";

export default function OverallClassReport() {
  const {
    selectedCompetition,
    selectedSchool,
    selectedClass,
    selectedStudent,
    lang,
  } = useSelector((state) => state.main);
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const dispatch = useDispatch();

  const fetchData = async () => {
    try {
      const res = await OverallStudentReportService(
        selectedCompetition.id,
        selectedClass.code,
        selectedStudent.id
      );

      //  filtering data
      const { accuracy, proficiency, marks } = res.data;

      let school_proficiency_status = handleCompPerformance(
        proficiency,
        selectedCompetition.proficiency
      );
      let class_proficiency_status = handleCompPerformance(
        proficiency,
        selectedSchool.proficiency
      );
      let student_proficiency_status = handleCompPerformance(
        proficiency,
        selectedClass.proficiency
      );

      let school_accuracy_status = handleCompPerformance(
        accuracy,
        selectedCompetition.accuracy
      );
      let class_accuracy_status = handleCompPerformance(
        accuracy,
        selectedSchool.accuracy
      );
      let student_accuracy_status = handleCompPerformance(
        accuracy,
        selectedClass.accuracy
      );

      let school_marks_status = handleCompPerformance(
        marks,
        selectedCompetition.marks
      );
      let class_marks_status = handleCompPerformance(
        marks,
        selectedSchool.marks
      );
      let student_marks_status = handleCompPerformance(
        marks,
        selectedClass.marks
      );

      const val = Object.assign(res.data, {
        school_proficiency_status,
        class_proficiency_status,
        student_proficiency_status,
        school_accuracy_status,
        class_accuracy_status,
        student_accuracy_status,
        school_marks_status,
        class_marks_status,
        student_marks_status,
      });

      setValues(val);
    } catch (error) {
      dispatch(configActions.showAlert(error?.data?.message));
    } finally {
      setIsFetching(false);
    }
  };

  const handleFloatParse = (val) => {
    return parseFloat(val).toFixed(2);
  };

  const competition_proficiency_status = (
    <ScoreView
      content={textIdentifier["overall-proficiency"][lang]+` ${parseFloat(
        selectedCompetition.proficiency
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.school_proficiency_status} />
      </div>
    </ScoreView>
  );

  const school_proficiency_status = (
    <ScoreView
      content={textIdentifier["school-proficiency"][lang]+` ${parseFloat(
        selectedSchool.proficiency
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.class_proficiency_status} />
      </div>
    </ScoreView>
  );

  const class_proficiency_status = (
    <ScoreView
      content={textIdentifier["class-proficiency"][lang]+` ${parseFloat(
        selectedClass.proficiency
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.student_proficiency_status} />
      </div>
    </ScoreView>
  );

  const competition_accuracy_status = (
    <ScoreView
      content={textIdentifier["overall-accuracy"][lang]+` ${parseFloat(
        selectedCompetition.accuracy
      ).toFixed(2)}%`}
    >
      <div>
        <Notation status={values.school_accuracy_status} />
      </div>
    </ScoreView>
  );

  const school_accuracy_status = (
    <ScoreView
      content={textIdentifier["school-accuracy"][lang]+` ${parseFloat(selectedSchool.accuracy).toFixed(
        2
      )}%`}
    >
      <div>
        <Notation status={values.class_accuracy_status} />
      </div>
    </ScoreView>
  );

  const class_accuracy_status = (
    <ScoreView
      content={textIdentifier["class-accuracy"][lang]+` ${parseFloat(selectedClass.accuracy).toFixed(
        2
      )}%`}
    >
      <div>
        <Notation status={values.student_accuracy_status} />
      </div>
    </ScoreView>
  );

  const competition_marks_status = (
    <ScoreView
      content={textIdentifier["overall-marks"][lang]+` ${parseFloat(selectedCompetition.marks).toFixed(
        2
      )}`}
    >
      <div>
        <Notation status={values.school_proficiency_status} />
      </div>
    </ScoreView>
  );

  const school_marks_status = (
    <ScoreView
      content={textIdentifier["school-marks"][lang]+` ${parseFloat(selectedSchool.marks).toFixed(2)}`}
    >
      <div>
        <Notation status={values.class_proficiency_status} />
      </div>
    </ScoreView>
  );

  const class_marks_status = (
    <ScoreView
      content={textIdentifier["class-marks"][lang]+` ${parseFloat(selectedClass.marks).toFixed(2)}`}
    >
      <div>
        <Notation status={values.student_proficiency_status} />
      </div>
    </ScoreView>
  );

  useEffect(() => {
    if (!selectedCompetition || !selectedSchool) {
      navigate("/");
    } else {
      fetchData();
    }
  }, []);

  if (!values && !isFetching) {
    return <p>{textIdentifier["no-data-found"][lang]}</p>;
  }

  return (
    <div className="cr-card">
      <ReportTable
        data={[
          [
            `${parseFloat(values.proficiency).toFixed(2)}%`,
            `${parseFloat(values.accuracy).toFixed(2)}%`,
            handleFloatParse(
              values.easy_question_count +
                values.medium_question_count +
                values.hard_question_count
            ),
            handleFloatParse(values.marks),
          ],
          [
            <div className="flex-center">
              {competition_proficiency_status}
              {school_proficiency_status}
              {class_proficiency_status}
            </div>,
            <div className="flex-center">
              {competition_accuracy_status}
              {school_accuracy_status}
              {class_accuracy_status}
            </div>,
            `(E${handleFloatParse(
              values.easy_question_count
            )}, M${handleFloatParse(
              values.medium_question_count
            )}, H${handleFloatParse(values.hard_question_count)})`,
            <div className="flex-center">
              {competition_marks_status}
              {school_marks_status}
              {class_marks_status}
            </div>,
          ],
        ]}
        headers={[
          [
            textIdentifier["class-rank"][lang]+` : ${values.class_rank}/${values.class_student_count}`,
            textIdentifier["class-percentile"][lang]+` : ${values.class_percentile}%`,
            textIdentifier["class-school-percentile"][lang]+` : ${values.school_percentile}%`,
            !selectedCompetition.hide_rank && textIdentifier["school-rank"][lang]+` : ${values.school_rank}/${values.school_student_count}`,
          ],
          [
           textIdentifier["competition-rank"][lang]+` : ${values.competition_rank}/${values.competition_student_count}`,
           textIdentifier["competition-percentile"][lang]+` : ${values.competition_percentile}%`,
           !!values.warmup_done?textIdentifier["warm-up"][lang]+` : ${values.warmup_done ? "done" : "Not done"}`:`Participated: ${values.participated?"Yes":"No"}`,
           !!values.warmup_done?textIdentifier["participated"][lang]+` : ${values.participated?"Yes":"No"}`:null,
          ],
          [textIdentifier["proficiency"][lang], textIdentifier["accuracy"][lang], textIdentifier["question-count"][lang], textIdentifier["marks"][lang]],
        ]}
      />
    </div>
  );
}
